import PropTypes from "prop-types";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import dayjs from "@shared/dayjs";
import Avatar from "@shared/v2/Avatar";
import IconButton from "@shared/v2/IconButton";
import TextLink from "@shared/v2/TextLink";
import Tooltip from "@shared/v2/Tooltip";
import { Popover } from "@shared/v2/Popover";
import {
  BoxArchiveSolidV6,
  EllipsisSolidV6,
  EyeSolidV6,
  LinkSolidV6,
  PencilSolidV6,
  ShareSolidV6,
} from "@shared/v2/Icomoon";
import { TD, TR } from "../../Reports/components/BusinessDashboard/Table/Table";
import { archivePage, getPages, getToken } from "../../reducers/websiteReducer/areaPageReducer";
import { useToken, useTokenExpiresAt } from "../../reducers/websiteReducer/areaPageSelectors";
import { setSnackbar } from "../../reducers/snackbarReducer";
import PostStatus from "../Blog/PostStatus";
import { isTokenValid } from "./api";

// One row in the Area Pages table — same layout as the blog's PostRow.
const AreaPageRow = ({ areaPage, setSharePage, site = undefined }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const openEditor = () => navigate(`/area_pages/${areaPage.uuid}`);
  const pageUrl = site
    ? `https://${site.subdomain ? `${site.subdomain}.` : ""}${site.domain}/area${areaPage.path || `/${areaPage.slug}`}`
    : "";
  const token = useToken();
  const tokenExpiresAt = useTokenExpiresAt();
  // Archived is a page-level flag (isActive), independent of the settings status it was
  // published/drafted under.
  const archiving = useRef(false);
  const archive = async (isActive) => {
    if (archiving.current) return;
    archiving.current = true;
    try {
      // The index can sit open past the token's 5 minutes; refresh before writing.
      if (!isTokenValid(token, tokenExpiresAt)) await dispatch(getToken()).unwrap();
      await dispatch(archivePage({ uuid: areaPage.uuid, isActive })).unwrap();
      await dispatch(getPages());
    } finally {
      archiving.current = false;
    }
  };

  const status = areaPage?.settings?.status;
  const page = { isPublished: status === "published", isArchived: areaPage.isActive === false };
  // The editor mirrors the featured image into the settings blob; a dedicated thumbnail
  // wins over it when the Thumbnail panel starts persisting one.
  const thumbnailUrl =
    areaPage?.settings?.settings?.thumbnailUrl ||
    areaPage?.settings?.settings?.featuredImageUrl ||
    areaPage.featuredImageUrl;

  return (
    <TR className="tw-h-[64px]">
      <TD border="tw-border-b-[1px] tw-border-r-0">
        {thumbnailUrl ? (
          <img
            className="tw-w-[52px] tw-max-w-[52px] tw-h-[40px] tw-object-cover tw-rounded-[4px]"
            src={thumbnailUrl}
            alt="Featured"
          />
        ) : (
          <div className="tw-w-[52px] tw-h-[40px] tw-object-cover tw-rounded-[4px] tw-bg-gray-50" />
        )}
      </TD>
      <TD border="tw-border-b-[1px] tw-border-r-0">
        <TextLink
          className="tw-w-full tw-whitespace-normal tw-line-clamp-2 tw-min-w-[200px]"
          type="button"
          onClick={openEditor}
        >
          {areaPage.name}
        </TextLink>
      </TD>
      <TD border="tw-border-b-[1px] tw-border-r-0">
        {areaPage?.settings?.variables?.vars?.find((variable) => variable.key === "location")?.value || (
          <span className="tw-text-gray-50">Custom</span>
        )}
      </TD>
      <TD border="tw-border-b-[1px] tw-border-r-0">
        <div className="tw-flex tw-justify-center">
          <PostStatus post={page} />
        </div>
      </TD>
      <TD border="tw-border-b-[1px] tw-border-r-0">
        {areaPage?.settings?.datePublished
          ? dayjs(areaPage.settings.datePublished).format("MMM D, YYYY")
          : "—"}
      </TD>
      <TD border="tw-border-b-[1px] tw-border-r-0">
        {areaPage.dateCreated ? dayjs(areaPage.dateCreated).format("MMM. D, YYYY") : "-"}
      </TD>
      <TD border="tw-border-b-[1px] tw-border-r-0">
        <div className="tw-flex tw-justify-center">
          <Tooltip
            trigger={
              <Avatar
                size="medium"
                src={areaPage.createdBy?.avatar}
                alt={`${areaPage.createdBy?.firstName ?? ""} ${areaPage.createdBy?.lastName ?? ""}`}
              />
            }
            content={`${areaPage.createdBy?.firstName ?? ""} ${areaPage.createdBy?.lastName ?? ""}`}
          />
        </div>
      </TD>
      <TD border="tw-border-b-[1px] tw-border-r-0" className="tw-text-end">
        <Popover placement="bottom-end">
          <Popover.Trigger>
            <IconButton schema="tertiary">
              <EllipsisSolidV6 />
            </IconButton>
          </Popover.Trigger>
          <Popover.Content>
            {/* Edit opens the editor; Share/Copy Link/View Page are follow-up tickets. */}
            {page.isArchived ? (
              <Popover.Item icon={<BoxArchiveSolidV6 />} onClick={() => archive(true)}>
                Unarchive
              </Popover.Item>
            ) : (
              <>
                <Popover.Item icon={<PencilSolidV6 />} onClick={openEditor}>
                  Edit
                </Popover.Item>
                <Popover.Item
                  icon={<ShareSolidV6 />}
                  onClick={() => setSharePage(areaPage)}
                  disabled={!page.isPublished}
                >
                  Share
                </Popover.Item>
                <Popover.Item
                  icon={<LinkSolidV6 />}
                  onClick={() =>
                    window.copyTextToClipboard(pageUrl).then(() =>
                      dispatch(
                        setSnackbar({
                          show: true,
                          position: "top",
                          text: "Link copied",
                          schema: "executed",
                          timeout: 5000,
                          closeOnTimeout: () => dispatch(setSnackbar({ show: false })),
                        }),
                      ),
                    )
                  }
                  disabled={!page.isPublished}
                >
                  Copy Link
                </Popover.Item>
                <Popover.Item
                  icon={<EyeSolidV6 />}
                  onClick={() => window.open(pageUrl, "_blank")}
                  disabled={!page.isPublished}
                >
                  View Page
                </Popover.Item>
                <Popover.Item icon={<BoxArchiveSolidV6 />} onClick={() => archive(false)}>
                  Archive
                </Popover.Item>
              </>
            )}
          </Popover.Content>
        </Popover>
      </TD>
    </TR>
  );
};

AreaPageRow.propTypes = {
  areaPage: PropTypes.shape().isRequired,
  setSharePage: PropTypes.func.isRequired,
  site: PropTypes.shape(),
};

export default AreaPageRow;
